import { useAuth } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { fetchPlans } from "@/api/entitlements";
import type { PlanCatalogueEntry } from "@/interfaces/entitlements";

/**
 * The plan catalogue — what is for sale, what each plan permits, and the copy that sells it.
 *
 * Every plan card, badge label and upsell renders from this rather than from a constant, so the
 * feature list a client reads and the limits the server enforces come from one place and cannot
 * drift. Effectively static, so it is cached for the session.
 */
export function usePlans() {
  const { isSignedIn } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["plans"],
    queryFn: fetchPlans,
    enabled: !!isSignedIn,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    // One retry, not the default three with backoff. The catalogue is a static read behind auth, so
    // the realistic failure is a rejected token rather than a blip — and retrying that for ten
    // seconds leaves the plan chooser in a loading state that reads as empty content.
    retry: 1,
  });

  const plans: PlanCatalogueEntry[] = data?.plans ?? [];
  const byKey = (key: string) => plans.find((p) => p.key === key);

  return { plans, byKey, isLoading };
}
